
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, TextField
} from "@mui/material";
import { useEffect, useState } from "react";
import { IoArrowBack } from "react-icons/io5";
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const MyRequest = () => {
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [cardsPerPage, setCardsPerPage] = useState(6);
  const [open, setOpen] = useState(false);
  const [currentMessage, setCurrentMessage] = useState(null);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const brandId = localStorage.getItem("brandID");
      const response = await fetch(`https://server-side-influencer.onrender.com/brand/${brandId}/messages`);
      const result = await response.json();

      if (result.success) {
        setData(result.data);
      } else {
        setData([]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const uploadProfileImage = async (imageFile, brandId) => {
    setLoading(true);
    const formData = new FormData();
    formData.append("image", imageFile);

    try {
      const res = await fetch(
        `https://server-side-influencer-production.up.railway.app/brand/uploadbrandimage/${brandId}`,
        {
          method: "POST",
          body: formData,
        }
      );

      const result = await res.json();
      if (result.success) {
        toast.success("Profile image uploaded successfully!");
        fetchMessages();
      } else {
        toast.error("Failed to upload profile image.");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Error uploading profile image.");
    } finally {
      setLoading(false);
    }
  };

  const uploadBackgroundImage = async (imageFile, brandId) => {
    setLoading(true);
    const formData = new FormData();
    formData.append("image", imageFile);

    try {
      const res = await fetch(
        `https://server-side-influencer-production.up.railway.app/brand/uploadbrandimages2/${brandId}`,
        {
          method: "POST",
          body: formData,
        }
      );

      const result = await res.json();
      if (result.success) {
        toast.success("Background image uploaded successfully!");
        fetchMessages();
      } else {
        toast.error("Failed to upload background image.");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Error uploading background image.");
    } finally {
      setLoading(false);
    }
  };

  const handleClickOpen = (message) => {
    setCurrentMessage(message);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleUpdateMessage = async () => {
    const {
      _id,
      brandId,
      message,
      category,
      language,
      location,
      platform,
      influencerCount,
      influencerFollowersCount,
      budget,
      youtubeCount
    } = currentMessage;

    const updatedMessage = {
      message,
      category,
      language,
      location,
      platform,
      influencerCount,
      influencerFollowersCount,
      budget,
      youtubeCount
    };

    try {
      const response = await fetch(
        `https://server-side-influencer-production.up.railway.app/brand/message/${_id}/${brandId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updatedMessage),
        }
      );

      const result = await response.json();
      if (result.success) {
        toast.success("Message updated successfully!");
        fetchMessages();
        handleClose();
      } else {
        toast.error("Failed to update the message.");
      }
    } catch (error) {
      console.error("Error updating message:", error);
      toast.error("An error occurred while updating the message.");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentMessage(prev => ({ ...prev, [name]: value }));
  };

  const deleteMessage = async (messageId) => {
    try {
      const response = await fetch(
        `https://server-side-influencer-production.up.railway.app/brand/message/${messageId}`,
        { method: "DELETE" }
      );
      const result = await response.json();

      if (result.success) {
        toast.success("Message deleted successfully!");
        setData(prev => prev.filter(item => item._id !== messageId));
      } else {
        toast.error("Failed to delete the message.");
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("An error occurred while deleting the message.");
    }
  };

  const indexOfLastCard = currentPage * cardsPerPage;
  const indexOfFirstCard = indexOfLastCard - cardsPerPage;
  const currentCards = data.slice(indexOfFirstCard, indexOfLastCard);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const totalPages = Math.ceil(data.length / cardsPerPage);

  useEffect(() => {
    const updateCardsPerPage = () => {
      setCardsPerPage(window.innerWidth <= 768 ? 4 : 6);
    };
    updateCardsPerPage();
    window.addEventListener("resize", updateCardsPerPage);
    return () => window.removeEventListener("resize", updateCardsPerPage);
  }, []);

  return (
    <div className="flex h-screen">
      <div className="bg-gray-100 text-black flex items-center justify-between p-4 fixed w-full top-0 left-0 z-50">
        <div onClick={() => navigate(-1)} className="cursor-pointer flex items-center">
          <IoArrowBack size={24} />
          <span className="ml-2">Back</span>
        </div>
        <div className="text-right">my requirements</div>
      </div>

      <div className="flex-1 ml-0 p-6 pt-16">
        <ToastContainer />
        <h1 className="text-center text-3xl font-bold mb-9">My Requirements</h1>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 ml-16">
          {loading ? (
            [...Array(cardsPerPage)].map((_, i) => (
              <div key={i} className="bg-white border rounded-lg shadow overflow-hidden">
                <Skeleton height={128} />
                <div className="p-5">
                  <Skeleton width="80%" height={24} />
                  <Skeleton width="60%" height={18} />
                  <Skeleton width="40%" height={18} />
                </div>
              </div>
            ))
          ) : currentCards.length > 0 ? (
            currentCards.map((item) => (
              <div key={item._id} className="relative bg-white border rounded-2xl shadow-lg overflow-hidden">
                {/* Background Image */}
                <div className="w-full h-40 relative">
                  <img src={item.brandBackgroundImage2} alt="bg" className="w-full h-full object-cover" />

                  {/* Edit Background Icon */}
                  <input
                    type="file"
                    accept="image/*"
                    id={`bg-upload-${item._id}`}
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const brandId = localStorage.getItem("brandID");
                        uploadBackgroundImage(file, brandId);
                      }
                    }}
                  />
                  <IconButton
                    className="!absolute top-2 right-2 bg-white bg-opacity-80"
                    size="small"
                    onClick={() => document.getElementById(`bg-upload-${item._id}`).click()}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>

                  {/* Profile Image */}
                  <div className="absolute bottom-[-2rem] left-4">
                    <div className="relative w-16 h-16">
                      <img
                        src={item.profileImage || item.brandLogo}
                        alt="profile"
                        className="w-16 h-16 rounded-full border-4 border-white shadow-md object-cover"
                      />
                      <input
                        type="file"
                        accept="image/*"
                        id={`pf-upload-${item._id}`}
                        style={{ display: "none" }}
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            const brandId = localStorage.getItem("brandID");
                            uploadProfileImage(file, brandId);
                          }
                        }}
                      />
                      <IconButton
                        className="!absolute bottom-0 right-0 bg-white p-1 shadow-md"
                        onClick={() => document.getElementById(`pf-upload-${item._id}`).click()}
                        style={{ transform: 'translate(25%, 25%)' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                </div>

                <div className="p-6 pt-12">
                  <h5 className="text-xl font-semibold text-gray-900 mb-1">{item.shopName}</h5>
                  <p className="text-gray-700 text-sm mb-1"><strong>Campaign Brief:</strong> {item.message}</p>
                  <p className="text-gray-700 text-sm mb-1"><strong>Category:</strong> {item.category}</p>
                  <p className="text-gray-700 text-sm mb-1"><strong>Budget:</strong> ₹{item.budget}</p>
                  <div className="absolute bottom-4 right-4 flex gap-2">
                    <IconButton onClick={() => handleClickOpen(item)} color="primary"><EditIcon /></IconButton>
                    <IconButton onClick={() => deleteMessage(item._id)} color="error"><DeleteIcon /></IconButton>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <p className="text-gray-500 italic text-center">No pending requests found.</p>
          )}
        </div>

        {totalPages > 1 && (
          <div className="flex justify-center mt-4">
            <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1} className="px-4 py-2 border rounded-l-md">&lt;</button>
            {[...Array(totalPages)].map((_, index) => (
              <button key={index} onClick={() => paginate(index + 1)} className={`px-4 py-2 border ${currentPage === index + 1 ? "bg-blue-500 text-white" : ""}`}>{index + 1}</button>
            ))}
            <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages} className="px-4 py-2 border rounded-r-md">&gt;</button>
          </div>
        )}
      </div>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Edit your requirement</DialogTitle>
        <DialogContent>
          {[
            "message", "category", "language", "location",
            "platform", "influencerCount", "influencerFollowersCount", "budget", "youtubeCount"
          ].map(field => (
            <TextField
              key={field}
              label={field.replace(/([A-Z])/g, " $1").replace(/^./, str => str.toUpperCase())}
              name={field}
              value={currentMessage?.[field] || ""}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
              type={field.toLowerCase().includes("count") || field === "budget" ? "number" : "text"}
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">Cancel</Button>
          <Button onClick={handleUpdateMessage} color="primary">Save</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default MyRequest;
